import { useState, useRef, useEffect } from 'react';
import { Flag, X, Check, Search, Trash2 } from 'lucide-react';
import { frameOrderApi } from '../../services/frameOrderApi';

interface FollowUpQuickActionProps {
    documentId: string;
    hasFollowUp: boolean;
    currentAssignee: string | null;
    defaultAssignee: string | null;
    assigneeOptions: string[];
    onChanged: () => void;
}

export function FollowUpQuickAction({
    documentId,
    hasFollowUp,
    currentAssignee,
    defaultAssignee,
    assigneeOptions,
    onChanged,
}: FollowUpQuickActionProps) {
    const [open, setOpen] = useState(false);
    const [assignee, setAssignee] = useState('');
    const [searchText, setSearchText] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // When popup opens, set the search text to the current/default assignee
    useEffect(() => {
        if (open) {
            const value = hasFollowUp ? (currentAssignee || '') : (defaultAssignee || '');
            setAssignee(value);
            setSearchText(value);
            setShowDropdown(false);
        }
    }, [open, hasFollowUp, currentAssignee, defaultAssignee]);

    useEffect(() => {
        if (!open) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    const filteredOptions = assigneeOptions.filter(name =>
        name.toLowerCase().includes(searchText.toLowerCase())
    );

    const handleSelectAssignee = (name: string) => {
        setAssignee(name);
        setSearchText(name);
        setShowDropdown(false);
    };

    const handleCreate = async () => {
        if (submitting) return;
        setSubmitting(true);
        try {
            await frameOrderApi.followUpAction(documentId, 'create', assignee || null);
            setOpen(false);
            onChanged();
        } catch (err) {
            console.error('Failed to create follow-up:', err);
        } finally {
            setSubmitting(false);
        }
    };

    const handleUpdate = async () => {
        if (submitting) return;
        setSubmitting(true);
        try {
            await frameOrderApi.followUpAction(documentId, 'update', assignee || null);
            setOpen(false);
            onChanged();
        } catch (err) {
            console.error('Failed to update follow-up:', err);
        } finally {
            setSubmitting(false);
        }
    };

    const handleRemove = async () => {
        if (submitting) return;
        setSubmitting(true);
        try {
            await frameOrderApi.followUpAction(documentId, 'remove');
            setOpen(false);
            onChanged();
        } catch (err) {
            console.error('Failed to remove follow-up:', err);
        } finally {
            setSubmitting(false);
        }
    };

    if (!open) {
        return (
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    setOpen(true);
                }}
                className={`inline-flex items-center rounded p-1 text-xs transition-colors ${
                    hasFollowUp
                        ? 'text-orange-500 hover:bg-orange-50'
                        : 'text-gray-400 hover:bg-orange-50 hover:text-orange-500'
                }`}
                title={hasFollowUp ? 'Edit follow-up' : 'Add follow-up'}
            >
                <Flag className="h-3.5 w-3.5" fill={hasFollowUp ? 'currentColor' : 'none'} />
            </button>
        );
    }

    const assigneeChanged = hasFollowUp && assignee !== (currentAssignee || '');

    return (
        <div
            ref={containerRef}
            className="absolute right-0 top-full z-20 mt-1 w-64 rounded-lg border border-gray-200 bg-white p-3 shadow-lg"
            onClick={(e) => e.stopPropagation()}
        >
            <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-medium text-gray-700">
                    {hasFollowUp ? 'Edit follow-up' : 'Create follow-up'}
                </span>
                <button
                    onClick={() => setOpen(false)}
                    className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                >
                    <X className="h-3.5 w-3.5" />
                </button>
            </div>

            {/* Assignee search — always shown */}
            <div className="relative mb-2">
                <label className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-gray-500">
                    Assigned to
                </label>
                <div className="relative">
                    <Search className="pointer-events-none absolute left-2 top-1.5 h-3.5 w-3.5 text-gray-400" />
                    <input
                        type="text"
                        value={searchText}
                        onChange={(e) => {
                            setSearchText(e.target.value);
                            setAssignee(e.target.value);
                            setShowDropdown(true);
                        }}
                        onFocus={() => setShowDropdown(true)}
                        placeholder="Search employee..."
                        className="h-7 w-full rounded border border-gray-300 bg-white pl-7 pr-2 text-xs text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                </div>
                {showDropdown && filteredOptions.length > 0 && (
                    <div className="absolute z-30 mt-0.5 max-h-32 w-full overflow-y-auto rounded border border-gray-200 bg-white shadow-md">
                        {filteredOptions.map(name => (
                            <button
                                key={name}
                                onClick={() => handleSelectAssignee(name)}
                                className={`w-full px-2 py-1 text-left text-xs hover:bg-blue-50 ${
                                    name === assignee ? 'bg-blue-50 font-medium text-blue-700' : 'text-gray-700'
                                }`}
                            >
                                {name}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Action buttons */}
            {hasFollowUp ? (
                <div className="space-y-1.5">
                    <button
                        onClick={handleUpdate}
                        disabled={submitting || !assigneeChanged}
                        className="inline-flex h-7 w-full items-center justify-center gap-1.5 rounded-md bg-orange-500 text-xs font-medium text-white transition-colors hover:bg-orange-600 disabled:opacity-50"
                    >
                        {submitting ? (
                            <div className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                        ) : (
                            <>
                                <Check className="h-3 w-3" />
                                Update assignee
                            </>
                        )}
                    </button>
                    <button
                        onClick={handleRemove}
                        disabled={submitting}
                        className="inline-flex h-7 w-full items-center justify-center gap-1.5 rounded-md border border-red-200 bg-red-50 text-xs font-medium text-red-700 transition-colors hover:bg-red-100 disabled:opacity-50"
                    >
                        <Trash2 className="h-3 w-3" />
                        Remove follow-up
                    </button>
                </div>
            ) : (
                <button
                    onClick={handleCreate}
                    disabled={submitting}
                    className="inline-flex h-7 w-full items-center justify-center gap-1.5 rounded-md bg-orange-500 text-xs font-medium text-white transition-colors hover:bg-orange-600 disabled:opacity-50"
                >
                    {submitting ? (
                        <div className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    ) : (
                        <>
                            <Check className="h-3 w-3" />
                            Set follow-up
                        </>
                    )}
                </button>
            )}
        </div>
    );
}
